import { consola } from 'consola'
import { Capacitor } from '@capacitor/core'

/**
 * Композабл для универсального хранения номера телефона заведения
 * Работает в браузере (localStorage) и мобильных приложениях (SecureStorage)
 * Используется для отображения номера на странице офлайн
 */
export const useInstitutionPhoneStorage = () => {
  const STORAGE_KEY = 'institution_phone'

  /**
   * Сохранить номер телефона заведения
   */
  const setInstitutionPhone = async (phone: string | null): Promise<void> => {
    try {
      if (!phone) {
        await removeInstitutionPhone()
        return
      }

      if (process.client && Capacitor.isNativePlatform()) {
        // Мобильное приложение - используем SecureStorage
        const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin')
        
        await SecureStoragePlugin.set({ 
          key: STORAGE_KEY, 
          value: phone, 
        })
        consola.debug('✅ Номер телефона заведения сохранен в SecureStorage:', phone)
      } else if (process.client) {
        // Браузер - используем localStorage
        localStorage.setItem(STORAGE_KEY, phone)
        consola.debug('✅ Номер телефона заведения сохранен в localStorage:', phone)
      }
    } catch (error) {
      consola.error('❌ Ошибка сохранения номера телефона заведения:', error)
      // Fallback на localStorage в случае ошибки
      if (process.client && phone) {
        try {
          localStorage.setItem(STORAGE_KEY, phone)
        } catch (fallbackError) {
          consola.error('❌ Ошибка fallback сохранения:', fallbackError)
        }
      }
    }
  }

  /**
   * Получить сохраненный номер телефона заведения
   */
  const getInstitutionPhone = async (): Promise<string | null> => {
    try {
      let phone: string | null = null

      if (process.client && Capacitor.isNativePlatform()) {
        // Мобильное приложение - используем SecureStorage
        const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin')
        
        try {
          const result = await SecureStoragePlugin.get({ key: STORAGE_KEY })
          phone = result?.value || null
          if (phone) {
            consola.debug('📱 Номер телефона заведения загружен из SecureStorage:', phone)
          }
        } catch {
          // Ключ не найден в SecureStorage - это нормально при первом запуске
          consola.debug('📱 Номер телефона заведения не найден в SecureStorage')
        }
      } else if (process.client) {
        // Браузер - используем localStorage
        phone = localStorage.getItem(STORAGE_KEY)
        if (phone) {
          consola.debug('🌐 Номер телефона заведения загружен из localStorage:', phone)
        }
      }

      return phone
    } catch (error) {
      consola.error('❌ Ошибка загрузки номера телефона заведения:', error)
      // Fallback на localStorage в случае ошибки
      if (process.client) {
        try {
          return localStorage.getItem(STORAGE_KEY)
        } catch {
          return null
        }
      }
      return null
    }
  }

  /**
   * Удалить сохраненный номер телефона заведения
   */
  const removeInstitutionPhone = async (): Promise<void> => {
    try {
      if (process.client && Capacitor.isNativePlatform()) {
        // Мобильное приложение - используем SecureStorage
        const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin')
        await SecureStoragePlugin.remove({ key: STORAGE_KEY })
        consola.debug('✅ Номер телефона заведения удален из SecureStorage')
      } else if (process.client) {
        // Браузер - используем localStorage
        localStorage.removeItem(STORAGE_KEY)
        consola.debug('✅ Номер телефона заведения удален из localStorage')
      }
    } catch (error) {
      consola.error('❌ Ошибка удаления номера телефона заведения:', error)
      // Fallback на localStorage в случае ошибки
      if (process.client) {
        try {
          localStorage.removeItem(STORAGE_KEY)
        } catch (fallbackError) {
          consola.error('❌ Ошибка fallback удаления:', fallbackError)
        }
      }
    }
  }

  /**
   * Проверить поддержку хранилища
   */
  const isStorageSupported = (): boolean => {
    if (!process.client) return false
    
    if (Capacitor.isNativePlatform()) {
      return true // SecureStorage всегда доступен в Capacitor
    }
    
    // Проверяем поддержку localStorage
    try {
      const test = 'storage_test'
      localStorage.setItem(test, test)
      localStorage.removeItem(test)
      return true
    } catch {
      return false
    }
  }

  /**
   * Получить тип используемого хранилища
   */
  const getStorageType = (): 'secure_storage' | 'local_storage' | 'none' => {
    if (!process.client) return 'none'
    
    if (Capacitor.isNativePlatform()) {
      return 'secure_storage'
    }
    
    return isStorageSupported() ? 'local_storage' : 'none'
  }

  return {
    setInstitutionPhone,
    getInstitutionPhone,
    removeInstitutionPhone,
    isStorageSupported,
    getStorageType,
  }
}
